import styled from '@emotion/styled';
import Link from 'next/link';

import { screen } from '@/components/common/breakpoints';
import { Image } from '@/components/common/image';
import { Heading, HeadingLevel } from '@/components/common/MarkUp';
import { PortableText } from '@/lib/sanity';
import { styledLink } from '@/styles/misc';

import type { BackgroundColourType } from '@/types/colours';
import type { SanityStandoutComponentType } from '@/types/standout';

export interface StandoutComponentProps
  extends Partial<SanityStandoutComponentType> {
  backgroundColour: BackgroundColourType;
}

export const StandoutComponent = ({
  title,
  text,
  image,
  ctaText,
  ctaUrl,
  trackingTag,
}: StandoutComponentProps) => {
  return (
    <HeroContainer>
      <HeadingLevel>
        {!!image && (
          <ImageContainer>
            <Image
              {...image}
              sizes="(max-width: 200px) 25vw, 200px"
              objectFit="contain"
              preserveMaxDimensions
            />
          </ImageContainer>
        )}
        <CopyContainer>
          <Heading size="headline-lg">{title}</Heading>
          <PortableText value={text} />
          {!!ctaUrl && !!ctaText && (
            <ButtonContainer>
              <StyledButton
                href={ctaUrl}
                className={
                  trackingTag?.blockName &&
                  `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
                }
              >
                {ctaText}
              </StyledButton>
            </ButtonContainer>
          )}
        </CopyContainer>
      </HeadingLevel>
    </HeroContainer>
  );
};

const HeroContainer = styled.div`
  display: flex;
  gap: var(--spacing-medium);
  flex-direction: column;

  background-color: var(--background-primary);
  border-radius: var(--radius-m);
  padding: var(--spacing-xx-large) var(--spacing-x-large);
  color: var(--text-inverse-strong);
  justify-content: space-between;

  ${screen.sm} {
    flex-direction: row;
  }

  * + & {
    margin-top: var(--spacing-large);
  }
`;

const CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 1;

  h1 {
    margin-bottom: var(--spacing-1600);
    margin-top: var(--spacing-800);
    padding-top: var(--spacing-small);
  }

  p {
    margin-bottom: var(--spacing-large);
  }

  ${screen.sm} {
    margin-top: 0;
    flex: 1 0 60%;

    h1 {
      margin-bottom: var(--spacing-500);
      margin-top: 0;
      padding-top: 0;
    }

    p {
      margin-bottom: var(--spacing-400);
    }
  }

  a {
    white-space: normal;
    height: auto;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-top;
  margin: 0px;

  ${screen.sm} {
    margin: 0 auto;
    flex: 1 0 40%;
    max-width: 196px;
    min-width: 0;
  }
`;
const StyledButton = styled(Link)`
  ${styledLink}

  font-size: inherit;
  color: var(--text-inverse-strong);
  transition: background-color 0.3s ease-out;

  &:hover {
    background-color: var(--surface-inverse-subtle);
  }
  &:visited {
    color: var(--text-inverse-strong);
  }
`;
